import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const EmailVerificationHandler = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [countdown, setCountdown] = useState(5);
  const hasChecked = useRef(false);

  useEffect(() => {
    // Only run once
    if (hasChecked.current) return;
    hasChecked.current = true;

    // Check if this is a verification callback from Supabase
    const hash = location.hash;
    
    // Supabase adds these hash params after email verification:
    // #access_token=...&expires_in=...&refresh_token=...&token_type=bearer&type=signup
    if (hash.includes('access_token') && hash.includes('type=signup')) {
      setStatus('success');
      
      // Countdown timer
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            // Redirect based on device
            redirectToApp();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    } else {
      // Not a verification URL, don't render anything
      setStatus('redirect');
    }
  }, [location, navigate]);

  const redirectToApp = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    
    // Detect mobile device
    const isMobile = /iPhone|iPad|iPod|Android/i.test(userAgent);
    
    if (isMobile) {
      // Try to open mobile app with deep link
      window.location.href = 'yesod://verified';
      
      // Fallback to app store if app doesn't open
      setTimeout(() => {
        const isIOS = /iPad|iPhone|iPod/.test(userAgent);
        if (isIOS) {
          window.location.href = 'https://apps.apple.com/app/yesod/id6479285373';
        } else {
          window.location.href = 'https://play.google.com/store/apps/details?id=com.yesod.app';
        }
      }, 2000);
    } else {
      // Desktop - go to web app home
      navigate('/');
    }
  };

  const handleOpenApp = () => {
    redirectToApp();
  };

  // Don't render anything if not a verification URL
  if (status === 'redirect' || status === 'verifying') {
    return null;
  }

  return (
    <div style={styles.container}>
      {/* Yesod Logo */}
      <svg style={styles.logo} viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
        <text x="50" y="70" fontFamily="Arial, sans-serif" fontSize="80" fontWeight="bold" textAnchor="middle" fill="#1A1A1A">Y</text>
      </svg>
      
      {/* Success Icon with Animated Checkmark */}
      <div style={styles.successIcon}>
        <svg style={styles.checkmark} viewBox="0 0 52 52">
          <polyline points="14 27 22 35 38 17" style={styles.checkmarkPath}/>
        </svg>
      </div>
      
      {/* Main Content */}
      <h1 style={styles.title}>Email Verified!</h1>
      <p style={styles.subtitle}>
        Your email has been successfully verified. You can now use all features of the Yesod app.
      </p>
      
      {/* CTA Button */}
      <button style={styles.button} onClick={handleOpenApp}>
        Open Yesod App
      </button>
      
      {/* Countdown */}
      <p style={styles.countdown}>
        Redirecting automatically in {countdown} seconds...
      </p>
      
      {/* Info Box */}
      <div style={styles.infoBox}>
        <h3 style={styles.infoTitle}>What's Next?</h3>
        <p style={styles.infoText}>
          You'll be redirected to the Yesod app. If you're on a mobile device, the app will open automatically. 
          If not installed, you'll be directed to the app store.
        </p>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    background: '#FFFFFF',
    textAlign: 'center',
  },
  logo: {
    width: '80px',
    height: 'auto',
    marginBottom: '32px',
  },
  successIcon: {
    width: '80px',
    height: '80px',
    margin: '0 auto 24px',
    background: 'linear-gradient(135deg, #5669FF 0%, #4454CC 100%)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 16px rgba(86, 105, 255, 0.3)',
  },
  checkmark: {
    width: '40px',
    height: '40px',
    stroke: 'white',
    strokeWidth: '3',
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    fill: 'none',
  },
  checkmarkPath: {
    animation: 'checkmark 0.6s ease-in-out 0.3s both',
    strokeDasharray: '100',
    strokeDashoffset: '100',
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1A1A1A',
    marginBottom: '12px',
    lineHeight: '1.2',
  },
  subtitle: {
    fontSize: '16px',
    color: '#666666',
    marginBottom: '32px',
    lineHeight: '1.5',
    maxWidth: '500px',
  },
  button: {
    display: 'inline-block',
    background: 'linear-gradient(135deg, #5669FF 0%, #4454CC 100%)',
    color: 'white',
    padding: '16px 40px',
    borderRadius: '12px',
    border: 'none',
    fontSize: '16px',
    fontWeight: '600',
    boxShadow: '0 4px 12px rgba(86, 105, 255, 0.3)',
    cursor: 'pointer',
    marginBottom: '16px',
    transition: 'all 0.2s ease',
  },
  countdown: {
    fontSize: '14px',
    color: '#888888',
    marginTop: '16px',
  },
  infoBox: {
    background: '#F9FAFB',
    borderRadius: '12px',
    padding: '16px',
    marginTop: '32px',
    textAlign: 'left',
    maxWidth: '500px',
  },
  infoTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1A1A1A',
    marginBottom: '8px',
  },
  infoText: {
    fontSize: '13px',
    color: '#666666',
    lineHeight: '1.5',
    margin: '0',
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #5669FF',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
};

// Add keyframe animation for checkmark
const styleSheet = document.styleSheets[0];
styleSheet.insertRule(`
  @keyframes checkmark {
    0% {
      stroke-dasharray: 100;
      stroke-dashoffset: 100;
    }
    100% {
      stroke-dasharray: 100;
      stroke-dashoffset: 0;
    }
  }
`, styleSheet.cssRules.length);

styleSheet.insertRule(`
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`, styleSheet.cssRules.length);

export default EmailVerificationHandler;
